import { httpClient } from '@/shared/api/httpClient';

/**
 * O status de uma OS é o `id` de uma etapa do kanban. Como a oficina cria
 * etapas próprias em Configurações, isso deixou de ser um conjunto fechado
 * — o tipo é `string`. Os ids que o sistema garante existir estão em
 * `ETAPA_INICIAL`, `ETAPA_FECHADO` e `ETAPA_CANCELADO` (`etapasKanban.ts`);
 * para rótulo e cor, use `useEtapasKanban()`.
 */
export type StatusOrcamento = string;

export type TipoItemOrcamento = 'Produto' | 'Servico';

export type TipoDesconto = 'Percentual' | 'Valor';

export type DescontoEntrada = { tipo: TipoDesconto; valor: number };

export type OrcamentoResumo = {
  id: string;
  numero: string;
  clienteId: string;
  clienteNome: string | null;
  data: string;
  prazoEntrega: string;
  status: StatusOrcamento;
  total: number;
  quantidadeItens: number;
  placa: string | null;
  /**
   * Como identificar o veículo na tela: a placa quando existe, senão
   * "Marca Modelo Ano" do veículo vinculado. Nunca vazio — prefira este
   * campo a `placa` em qualquer exibição.
   */
  veiculoDescricao: string;
  veiculoId: string | null;
  /** KM medido nesta ordem. `null` quando a ordem não registrou leitura. */
  kmRegistrado: number | null;
  colaboradorResponsavelId: string | null;
  colaboradorResponsavelNome: string | null;
  /**
   * Quando a O.S. foi finalizada (ISO). `null` enquanto não finalizada.
   * Base de data "Fechamento" dos relatórios.
   */
  finalizadoEm: string | null;
  /** true se o número visível é provisório (ORC-PEND-XXX), aguardando servidor. */
  syncPendente: boolean;
};

export type ItemOrcamentoEntrada = {
  tipo: TipoItemOrcamento;
  /** null ⇒ backend cadastra Produto/Serviço novo. */
  referenciaId: string | null;
  descricao: string;
  /** Código informado ao cadastrar peça nova (código de barras). Opcional. */
  codigo?: string | null;
  quantidade: number;
  precoUnitario: number;
  desconto?: DescontoEntrada | null;
  colaboradorId?: string | null;
};

export type ClienteNovoEntrada = { nome: string };

export type CriarOrcamentoRequest = {
  /** null/omitido ⇒ usa clienteNovo para criar inline. */
  clienteId: string | null;
  prazoEntrega: string;
  observacoes?: string | null;
  itens: ItemOrcamentoEntrada[];
  placa: string | null;
  veiculoId?: string | null;
  clienteNovo?: ClienteNovoEntrada | null;
  descontoGeral?: DescontoEntrada | null;
  colaboradorResponsavelId?: string | null;
  kmRegistrado?: number | null;
};

export type CriarOrcamentoResponse = { id: string; numero: string; total: number };

export type AtualizarOrcamentoRequest = {
  prazoEntrega: string;
  observacoes?: string | null;
  itens: ItemOrcamentoEntrada[];
  descontoGeral?: DescontoEntrada | null;
  colaboradorResponsavelId?: string | null;
};

export type OrcamentoItemDetalhe = {
  id: string;
  tipo: TipoItemOrcamento;
  referenciaId: string;
  descricao: string;
  quantidade: number;
  precoUnitario: number;
  descontoTipo: TipoDesconto | null;
  descontoValor: number | null;
  colaboradorId: string | null;
  colaboradorNome: string | null;
  subtotalBruto: number;
  valorDesconto: number;
  subtotal: number;
};

export type OrcamentoDetalhe = {
  id: string;
  numero: string;
  status: StatusOrcamento;
  data: string;
  prazoEntrega: string;
  observacoes: string | null;
  clienteId: string;
  clienteNome: string;
  clienteDocumento: string | null;
  clienteEmail: string | null;
  clienteTelefone: string | null;
  veiculoId: string | null;
  placa: string | null;
  /** Placa quando existe; senão "Marca Modelo Ano". Nunca vazio. */
  veiculoDescricao: string;
  /**
   * Quilometragem da ordem: o km informado na abertura. Quando a OS não
   * registrou km, vem o km atual do veículo. `null` só quando não há número
   * nenhum para aquele veículo (ou a OS é sem veículo).
   */
  kmRegistrado: number | null;
  colaboradorResponsavelId: string | null;
  colaboradorResponsavelNome: string | null;
  descontoGeralTipo: TipoDesconto | null;
  descontoGeralValor: number | null;
  subtotal: number;
  totalPecas: number;
  totalServicos: number;
  valorDescontoGeral: number;
  total: number;
  itens: OrcamentoItemDetalhe[];
};

export const orcamentosApi = {
  listar: () => httpClient.get<OrcamentoResumo[]>('/api/orcamentos'),
  obter: (id: string) => httpClient.get<OrcamentoDetalhe>(`/api/orcamentos/${id}`),
  criar: (cmd: CriarOrcamentoRequest) =>
    httpClient.post<CriarOrcamentoResponse>('/api/orcamentos', cmd),
  atualizar: (id: string, cmd: AtualizarOrcamentoRequest) =>
    httpClient.put<void>(`/api/orcamentos/${id}`, cmd),
  alterarStatus: (id: string, status: StatusOrcamento) =>
    httpClient.post<void>(`/api/orcamentos/${id}/status`, { status }),
  excluir: (id: string) => httpClient.delete<void>(`/api/orcamentos/${id}`),
  /**
   * Registra — ou corrige — o KM medido nesta ordem. A leitura fica amarrada à
   * OS: corrigir reescreve a mesma linha do histórico do veículo, não empilha
   * outra leitura da mesma visita.
   */
  registrarKm: (id: string, km: number) =>
    httpClient.post<{ id: string }>(`/api/orcamentos/${id}/km`, { km }),
  /** Gera/renova o link público de acompanhamento da OS (token com validade configurável, 1–30 dias). */
  gerarLinkAcompanhamento: (id: string) =>
    httpClient.post<{ url: string }>(`/api/orcamentos/${id}/link-acompanhamento`, {}),
};
